import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import managedAxios from "@/utils/managedAxios";
import { API_BASE } from "@/services/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip as RTooltip,
  LineChart, Line,
} from "recharts";
import {
  Target, TrendingDown, TrendingUp, Minus, BookOpen, ChevronDown, ArrowLeft,
  Loader2, Compass, ArrowUpRight, ArrowDownRight,
} from "lucide-react";

const PAPER_COLORS = { GS1: "#2563EB", GS2: "#7c3aed", GS3: "#0891b2", GS4: "#d97706", ESSAY: "#db2777", OTHER: "#64748b" };

function ScoreRing({ pct }) {
  const r = 48, c = 2 * Math.PI * r;
  const off = c - (pct / 100) * c;
  const color = pct >= 66 ? "#059669" : pct >= 40 ? "#d97706" : "#e11d48";
  return (
    <svg width="120" height="120" viewBox="0 0 120 120" className="shrink-0">
      <circle cx="60" cy="60" r={r} stroke="#eef1f5" strokeWidth="11" fill="none" />
      <circle cx="60" cy="60" r={r} stroke={color} strokeWidth="11" fill="none"
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
        transform="rotate(-90 60 60)" style={{ transition: "stroke-dashoffset 1s ease" }} />
      <text x="60" y="56" textAnchor="middle" fontSize="24" fontWeight="700" fill="#0f172a">{pct}%</text>
      <text x="60" y="76" textAnchor="middle" fontSize="11" fill="#64748b">avg score</text>
    </svg>
  );
}

function heat(count, max) {
  const t = max ? count / max : 0;
  if (t >= 0.66) return "bg-rose-100 text-rose-800 border-rose-200";
  if (t >= 0.33) return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
}

const TREND_STYLE = {
  improving: { Icon: ArrowUpRight, cls: "text-emerald-600 bg-emerald-50", stroke: "#059669", label: "improving" },
  declining: { Icon: ArrowDownRight, cls: "text-rose-600 bg-rose-50", stroke: "#e11d48", label: "declining" },
  steady: { Icon: Minus, cls: "text-slate-500 bg-slate-100", stroke: "#64748b", label: "steady" },
  new: { Icon: Minus, cls: "text-slate-400 bg-slate-50", stroke: "#94a3b8", label: "new" },
};

function TrendBadge({ trend }) {
  const s = TREND_STYLE[trend?.direction] || TREND_STYLE.new;
  const Ic = s.Icon;
  const delta = trend?.delta || 0;
  const sign = delta > 0 ? "+" : "";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${s.cls}`}>
      <Ic className="w-3.5 h-3.5" />
      {trend?.direction === "new" ? "new" : `${sign}${delta} pts`}
    </span>
  );
}

function Sparkline({ series, stroke }) {
  return (
    <div style={{ width: 120, height: 34 }}>
      <ResponsiveContainer>
        <LineChart data={series} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
          <Line type="monotone" dataKey="value" stroke={stroke} strokeWidth={2} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function WeaknessMapPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [openNode, setOpenNode] = useState(null);
  const [tab, setTab] = useState("snapshot");
  const [trends, setTrends] = useState(null);
  const [trendsLoading, setTrendsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await managedAxios.get(`${API_BASE}/intelligence/learning-graph`);
        setData(data);
      } catch (e) {
        setData({ total_nodes: 0, nodes: [], summary: null });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (tab === "trends" && !trends) {
      setTrendsLoading(true);
      managedAxios.get(`${API_BASE}/intelligence/trends`)
        .then(({ data }) => setTrends(data))
        .catch(() => setTrends({ total_evaluations: 0, dimensions: [], by_paper: [] }))
        .finally(() => setTrendsLoading(false));
    }
  }, [tab, trends]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
      </div>
    );
  }

  const summary = data?.summary;
  const nodes = data?.nodes || [];
  const empty = !summary || summary.total_attempts === 0;
  const maxWeak = summary?.top_weak_dimensions?.[0]?.count || 1;
  const maxFact = summary?.top_missing_facts?.[0]?.count || 1;

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="weakness-map-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4" data-testid="weakness-back-btn">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center">
            <Compass className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">My Weakness Map</h1>
            <p className="text-sm text-slate-500">Built from every answer you've had evaluated — across GS papers and essays</p>
          </div>
        </div>

        {!empty && (
          <div className="inline-flex p-1 bg-slate-100 rounded-lg mb-5" data-testid="weakness-tabs">
            {[["snapshot", "Snapshot"], ["trends", "Trends"]].map(([key, label]) => (
              <button key={key} onClick={() => setTab(key)}
                data-testid={`weakness-tab-${key}`}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${tab === key ? "bg-white shadow text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
                {label}
              </button>
            ))}
          </div>
        )}

        {empty ? (
          <Card className="border-dashed border-slate-300">
            <CardContent className="p-12 flex flex-col items-center text-center text-slate-500">
              <Target className="w-10 h-10 mb-3 text-slate-300" />
              <p className="font-medium text-slate-700">No evaluations yet</p>
              <p className="text-sm mt-1 mb-4">Evaluate a GS answer or essay and your weak dimensions and missed authorities will map here.</p>
              <Button onClick={() => navigate("/gs2-suite")} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="weakness-start-btn">
                Evaluate an answer
              </Button>
            </CardContent>
          </Card>
        ) : tab === "trends" ? (
          <TrendsView trends={trends} loading={trendsLoading} />
        ) : (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            {/* Overview + by-paper */}
            <div className="grid md:grid-cols-3 gap-5">
              <Card className="border-slate-200 shadow-sm md:col-span-1">
                <CardContent className="p-5 flex items-center gap-4">
                  <ScoreRing pct={summary.overall_avg_percentage} />
                  <div>
                    <div className="text-3xl font-bold text-slate-900" data-testid="weakness-total-attempts">{summary.total_attempts}</div>
                    <p className="text-sm text-slate-500">answers evaluated</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 shadow-sm md:col-span-2">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-800 text-sm mb-3">Performance by paper</h3>
                  <div style={{ width: "100%", height: 160 }} data-testid="weakness-paper-chart">
                    <ResponsiveContainer>
                      <BarChart data={summary.by_paper} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                        <XAxis dataKey="paper" tick={{ fontSize: 12, fill: "#475569" }} axisLine={false} tickLine={false} />
                        <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                        <RTooltip cursor={{ fill: "#f1f5f9" }} formatter={(v) => [`${v}%`, "avg"]} />
                        <Bar dataKey="avg_percentage" radius={[6, 6, 0, 0]} maxBarSize={54}>
                          {summary.by_paper.map((d, i) => (
                            <Cell key={i} fill={PAPER_COLORS[d.paper] || PAPER_COLORS.OTHER} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recurring weak dimensions */}
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingDown className="w-4 h-4 text-rose-600" />
                  <h3 className="font-semibold text-slate-800 text-sm">Your recurring blind spots</h3>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="weakness-dimensions">
                  {summary.top_weak_dimensions.map((d, i) => (
                    <span key={i} className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border ${heat(d.count, maxWeak)}`}>
                      {d.label}
                      <span className="font-bold">×{d.count}</span>
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Missed authorities/examples */}
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="w-4 h-4 text-[#2563EB]" />
                  <h3 className="font-semibold text-slate-800 text-sm">Authorities &amp; examples you keep missing</h3>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="weakness-facts">
                  {summary.top_missing_facts.map((f, i) => (
                    <span key={i} className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border ${heat(f.count, maxFact)}`}>
                      {f.value}
                      <span className="font-bold">×{f.count}</span>
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Topic drill-down (weakest first) */}
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <h3 className="font-semibold text-slate-800 text-sm mb-3">Topics — weakest first</h3>
                <div className="space-y-2" data-testid="weakness-topics">
                  {nodes.map((n, i) => {
                    const isOpen = openNode === i;
                    const pct = n.avg_percentage || 0;
                    const color = pct >= 66 ? "text-emerald-600" : pct >= 40 ? "text-amber-600" : "text-rose-600";
                    return (
                      <div key={i} className="border border-slate-200 rounded-lg overflow-hidden">
                        <button onClick={() => setOpenNode(isOpen ? null : i)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left" data-testid={`weakness-topic-${i}`}>
                          <span className="text-xs font-medium text-slate-400 uppercase w-14">{(n.paper || "").toUpperCase()}</span>
                          <span className="flex-1 text-sm text-slate-700 capitalize truncate">{(n.node_key || "").replace(/_/g, " ")}</span>
                          <span className="text-xs text-slate-400">{n.attempts} att</span>
                          <span className={`text-sm font-bold ${color}`}>{pct}%</span>
                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        </button>
                        {isOpen && (
                          <div className="px-4 pb-4 pt-1 bg-slate-50/60 space-y-3">
                            {n.top_weak_dimensions?.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-rose-700 mb-1">Weak dimensions</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {n.top_weak_dimensions.map((d, j) => (
                                    <span key={j} className="text-xs px-2 py-0.5 rounded bg-rose-100 text-rose-700">{d}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {n.top_missing_facts?.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold text-slate-600 mb-1">Frequently missed</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {n.top_missing_facts.map((f, j) => (
                                    <span key={j} className="text-xs px-2 py-0.5 rounded border border-slate-200 text-slate-600">{f}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}


function TrendsView({ trends, loading }) {
  if (loading || !trends) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-7 h-7 animate-spin text-[#2563EB]" />
      </div>
    );
  }
  if (trends.total_evaluations < 2) {
    return (
      <Card className="border-dashed border-slate-300">
        <CardContent className="p-12 text-center text-slate-500">
          <TrendingUp className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium text-slate-700">Not enough attempts yet</p>
          <p className="text-sm mt-1">Evaluate a few more answers and your trends will appear here.</p>
        </CardContent>
      </Card>
    );
  }
  const overall = trends.overall;
  const ovStyle = TREND_STYLE[overall.trend?.direction] || TREND_STYLE.new;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="weakness-trends">
      {/* Overall score over time */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-[#2563EB]" />
            <h3 className="font-semibold text-slate-800 text-sm">Overall score over time</h3>
            <span className="ml-auto"><TrendBadge trend={overall.trend} /></span>
          </div>
          <div style={{ width: "100%", height: 200 }} data-testid="weakness-overall-chart">
            <ResponsiveContainer>
              <LineChart data={overall.series} margin={{ top: 5, right: 12, left: -18, bottom: 0 }}>
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <RTooltip formatter={(v) => [`${v}%`, "score"]} />
                <Line type="monotone" dataKey="value" stroke={ovStyle.stroke} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Per-paper direction */}
      {trends.by_paper?.length > 0 && (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-800 text-sm mb-3">By paper</h3>
            <div className="flex flex-wrap gap-3" data-testid="weakness-paper-trends">
              {trends.by_paper.map((p, i) => (
                <div key={i} className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2">
                  <span className="text-sm font-semibold text-slate-700 w-14">{p.paper}</span>
                  <Sparkline series={p.series} stroke={(TREND_STYLE[p.trend?.direction] || TREND_STYLE.new).stroke} />
                  <TrendBadge trend={p.trend} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-dimension trends */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-5">
          <h3 className="font-semibold text-slate-800 text-sm mb-1">Dimension-by-dimension</h3>
          <p className="text-xs text-slate-400 mb-3">Are you actually getting better at each skill?</p>
          <div className="divide-y divide-slate-100" data-testid="weakness-dim-trends">
            {trends.dimensions.map((d, i) => {
              const s = TREND_STYLE[d.trend?.direction] || TREND_STYLE.new;
              return (
                <div key={i} className="flex items-center gap-3 py-2.5">
                  <span className="flex-1 text-sm text-slate-700 capitalize truncate">{d.label}</span>
                  <span className="text-xs text-slate-400 w-12 text-right">{d.attempts} att</span>
                  <Sparkline series={d.series} stroke={s.stroke} />
                  <span className="w-24 flex justify-end"><TrendBadge trend={d.trend} /></span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
